import { z } from 'zod/v4';

import { throwErrorUnlessProduction } from '../errors/throwErrorUnlessProduction';

/** Sorted by group: Count > Length > Weight > Volume. Keep this order when adding new units. */
export const QUANTITY_UNITS = [
    'Pieces',
    'Meters',
    'Centimeters',
    'Millimeters',
    'Feet',
    'Inches',
    'Kilograms',
    'Grams',
    'Milligrams',
    'Pounds',
    'Liters',
    'Centiliters',
    'Milliliters',
    'UsGallons',
    'UsPints',
    'UsFluidOunces',
] as const;

export type QuantityUnit = (typeof QUANTITY_UNITS)[number];

const QUANTITY_TYPES = ['CardinalNumber', 'Weight', 'Length', 'Volume'] as const;
export type QuantityType = (typeof QUANTITY_TYPES)[number];

export type Quantity = {
    type: QuantityType;
    unit: QuantityUnit;
    value: string;
};

export const QuantityRuntype: z.ZodType<Quantity> = z.object({
    type: z.enum(QUANTITY_TYPES),
    unit: z.enum(QUANTITY_UNITS),
    value: z.string(),
});

export const QUANTITY_BASE_UNITS = ['Kg', 'Meters', 'Liters', 'Pieces'] as const;
export type QuantityBaseUnit = (typeof QUANTITY_BASE_UNITS)[number];

/** @deprecated Use `QuantityBaseUnit`. */
export enum QuantityUnitOld {
    Kg = 'Kg',
    Meters = 'Meters',
    Liters = 'Liters',
    Pieces = 'Pieces',
}
/** @deprecated Use `QuantityBaseUnit`. */
export type QuantityUnitTypeOld = z.infer<typeof QuantityUnitRuntypeOld>;
/** @deprecated Prefer `QuantityRuntype` / new `Quantity` DTO. */
export const QuantityUnitRuntypeOld = z.enum(['Kg', 'Meters', 'Liters', 'Pieces']);

/** @deprecated Prefer `Quantity`. For legacy code, use `QuantityUnitDTOOldHelper`. */
export type QuantityUnitDTOOld = z.infer<typeof QuantityUnitDTORuntypeOld>;
/** @deprecated Prefer `QuantityRuntype`. For legacy code, use `QuantityUnitDTOOldHelper`. */
export const QuantityUnitDTORuntypeOld = z.object({
    unit: QuantityUnitRuntypeOld,
    quantity: z.number(),
});

const QUANTITY_TYPE_BASE_UNIT_MAP: Record<QuantityType, QuantityBaseUnit> = {
    CardinalNumber: 'Pieces',
    Weight: 'Kg',
    Length: 'Meters',
    Volume: 'Liters',
};

type ConversionEntry = { type: QuantityType; toBaseFactor: number };

const UNIT_CONVERSION_FACTORS: Record<QuantityUnit, ConversionEntry> = {
    Pieces: { type: 'CardinalNumber', toBaseFactor: 1 },
    Kilograms: { type: 'Weight', toBaseFactor: 1 },
    Grams: { type: 'Weight', toBaseFactor: 0.001 },
    Milligrams: { type: 'Weight', toBaseFactor: 0.000001 },
    Pounds: { type: 'Weight', toBaseFactor: 0.45359237 },
    Meters: { type: 'Length', toBaseFactor: 1 },
    Centimeters: { type: 'Length', toBaseFactor: 0.01 },
    Millimeters: { type: 'Length', toBaseFactor: 0.001 },
    Feet: { type: 'Length', toBaseFactor: 0.3048 },
    Inches: { type: 'Length', toBaseFactor: 0.0254 },
    Liters: { type: 'Volume', toBaseFactor: 1 },
    Centiliters: { type: 'Volume', toBaseFactor: 0.01 },
    Milliliters: { type: 'Volume', toBaseFactor: 0.001 },
    UsFluidOunces: { type: 'Volume', toBaseFactor: 0.0295735295625 },
    UsGallons: { type: 'Volume', toBaseFactor: 3.785411784 },
    UsPints: { type: 'Volume', toBaseFactor: 0.473176473 },
};

const PRIMARY_UNIT_FOR_TYPE: Record<QuantityType, QuantityUnit> = {
    CardinalNumber: 'Pieces',
    Weight: 'Kilograms',
    Length: 'Meters',
    Volume: 'Liters',
};

/** Maps legacy base unit (`QuantityUnitTypeOld`) to new `Quantity` type + primary unit. */
const BASE_UNIT_TO_TYPE_AND_PRIMARY: Record<QuantityBaseUnit, { type: QuantityType; unit: QuantityUnit }> = {
    Pieces: { type: 'CardinalNumber', unit: 'Pieces' },
    Kg: { type: 'Weight', unit: 'Kilograms' },
    Meters: { type: 'Length', unit: 'Meters' },
    Liters: { type: 'Volume', unit: 'Liters' },
};

export class QuantityHelper {
    private readonly qty: Quantity;

    constructor(qty: Quantity) {
        this.qty = qty;
    }

    private safeParseValue(): number {
        const num = Number(this.qty.value);
        if (isNaN(num) || !isFinite(num)) {
            throwErrorUnlessProduction(
                new Error(`QuantityHelper: invalid value "${this.qty.value}" for unit ${this.qty.unit}`),
            );
            return 0;
        }
        return num;
    }

    /** @deprecated Dangerous: use add/convert/normalize instead. Returns 0 on a parse error in production. */
    parseValue(): number {
        return this.safeParseValue();
    }

    /** Returns the legacy base unit (Kg/Meters/Liters/Pieces) for this quantity's type. */
    getBaseUnit(): QuantityBaseUnit {
        return QUANTITY_TYPE_BASE_UNIT_MAP[this.qty.type];
    }

    /** Sums in the primary unit; both must have the same type. */
    add(other: Quantity): QuantityHelper {
        if (this.qty.type !== other.type) {
            throwErrorUnlessProduction(new Error(`QuantityHelper.add: cannot add ${this.qty.type} and ${other.type}`));
            return QuantityHelper.zero(this.qty.type);
        }
        const a = this.normalize();
        const b = QuantityHelper.from(other).normalize();
        const sum = a.safeParseValue() + b.safeParseValue();
        return new QuantityHelper({
            type: this.qty.type,
            unit: a.qty.unit,
            value: String(sum),
        });
    }

    scale(factor: number): QuantityHelper {
        let new_value = this.safeParseValue() * factor;
        return new QuantityHelper({
            type: this.qty.type,
            unit: this.qty.unit,
            value: String(new_value),
        });
    }

    convert(targetUnit: QuantityUnit): QuantityHelper {
        const targetInfo = UNIT_CONVERSION_FACTORS[targetUnit];
        if (targetInfo.type !== this.qty.type) {
            throwErrorUnlessProduction(
                new Error(
                    `QuantityHelper.convert: cannot convert ${this.qty.unit} (${this.qty.type}) to ${targetUnit} (${targetInfo.type})`,
                ),
            );
            return QuantityHelper.zero(this.qty.type);
        }
        const sourceInfo = UNIT_CONVERSION_FACTORS[this.qty.unit];
        const valueInBase = this.safeParseValue() * sourceInfo.toBaseFactor;
        const valueInTarget = valueInBase / targetInfo.toBaseFactor;
        return new QuantityHelper({
            type: this.qty.type,
            unit: targetUnit,
            value: String(valueInTarget),
        });
    }

    /** Normalize to the primary unit for this quantity type (Kilograms/Meters/Liters/Pieces). */
    normalize(): QuantityHelper {
        return this.convert(PRIMARY_UNIT_FOR_TYPE[this.qty.type]);
    }

    build(): Quantity {
        return { ...this.qty };
    }

    convertToOld(): QuantityUnitDTOOld {
        const quantity = this.normalize();
        return {
            unit: this.getBaseUnit(),
            quantity: quantity.parseValue(),
        };
    }

    static from(qty: Quantity): QuantityHelper {
        return new QuantityHelper(qty);
    }

    static zero(type: QuantityType): QuantityHelper {
        return new QuantityHelper({
            type,
            unit: PRIMARY_UNIT_FOR_TYPE[type],
            value: '0',
        });
    }

    /** Create a QuantityHelper from a numeric value and a unit (accepts both new and legacy base units). */
    static create(value: number, unit: QuantityUnit | QuantityBaseUnit): QuantityHelper {
        if (unit in UNIT_CONVERSION_FACTORS) {
            const { type } = UNIT_CONVERSION_FACTORS[unit as QuantityUnit];
            return new QuantityHelper({ type, unit: unit as QuantityUnit, value: String(value) });
        }
        const { type, unit: quantityUnit } = BASE_UNIT_TO_TYPE_AND_PRIMARY[unit as QuantityBaseUnit];
        return new QuantityHelper({ type, unit: quantityUnit, value: String(value) });
    }

    /** @deprecated Use `QuantityHelper.create(value, unit)` instead. */
    static fromQuantityUnitToQuantityType(quantityUnit: QuantityUnit): QuantityType {
        return UNIT_CONVERSION_FACTORS[quantityUnit].type;
    }
}

/** @deprecated Use `QuantityHelper` with the `Quantity` type. */
export class QuantityUnitDTOOldHelper {
    private readonly dto: QuantityUnitDTOOld;

    private constructor(dto: QuantityUnitDTOOld) {
        this.dto = dto;
    }

    /** @deprecated */
    static from(dto: QuantityUnitDTOOld): QuantityUnitDTOOldHelper {
        return new QuantityUnitDTOOldHelper(dto);
    }

    /** @deprecated */
    static zero(unit: QuantityUnitTypeOld): QuantityUnitDTOOldHelper {
        return new QuantityUnitDTOOldHelper({ unit, quantity: 0 });
    }

    static toQuantityOrUndefined(dto: QuantityUnitDTOOld | null | undefined): Quantity | undefined {
        if (dto == null) {
            return undefined;
        }
        return QuantityUnitDTOOldHelper.from(dto).toQuantity();
    }

    /** In the primary unit per dimension: Kilograms, Meters, Liters or Pieces. */
    toQuantity(): Quantity {
        const { type, unit } = BASE_UNIT_TO_TYPE_AND_PRIMARY[this.dto.unit];
        return {
            type,
            unit,
            value: String(this.dto.quantity),
        };
    }

    /** @deprecated A no-op, so call sites already chain `.normalize()` for the switch to QuantityHelper. */
    normalize(): QuantityUnitDTOOldHelper {
        return this;
    }

    /** @deprecated Keeps the unit. */
    scale(factor: number): QuantityUnitDTOOldHelper {
        return new QuantityUnitDTOOldHelper({ unit: this.dto.unit, quantity: this.dto.quantity * factor });
    }

    /** @deprecated Throws outside production if the units differ. */
    add(other: QuantityUnitDTOOld): QuantityUnitDTOOldHelper {
        if (this.dto.unit !== other.unit) {
            throwErrorUnlessProduction(
                new Error(`QuantityUnitDTOOldHelper.add: cannot add ${this.dto.unit} and ${other.unit}`),
            );
            return this;
        }
        return new QuantityUnitDTOOldHelper({ unit: this.dto.unit, quantity: this.dto.quantity + other.quantity });
    }

    /** @deprecated Throws outside production if the units differ. */
    subtract(other: QuantityUnitDTOOld): QuantityUnitDTOOldHelper {
        if (this.dto.unit !== other.unit) {
            throwErrorUnlessProduction(
                new Error(`QuantityUnitDTOOldHelper.subtract: cannot subtract ${other.unit} from ${this.dto.unit}`),
            );
            return this;
        }
        return new QuantityUnitDTOOldHelper({ unit: this.dto.unit, quantity: this.dto.quantity - other.quantity });
    }

    /** @deprecated Becomes `QuantityHelper.parseValue()` after the migration. */
    value(): number {
        return this.dto.quantity;
    }

    /** @deprecated */
    isEqual(other: QuantityUnitDTOOld): boolean {
        return this.dto.unit === other.unit && this.dto.quantity === other.quantity;
    }

    /** @deprecated */
    build(): QuantityUnitDTOOld {
        return { ...this.dto };
    }
}
